import { IDocumentFileModel } from '@api/models';
import { renameDocumentFile } from '@api/services/document.service';
import { LoadingButton } from '@components/buttons';
import { Button, Drawer, Label } from '@hyperflake/react-ui-library';
import { FormHelperText } from '@library/forms';
import { useMutation } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput } from 'modules/shared/features/formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

interface RenameFileProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    file: IDocumentFileModel;
    refetchAll?: () => void;
}

const RenameFileSchema = Yup.object({
    name: Yup.string().trim().required('This field is required'),
});

const RenameFileDrawer = (props: RenameFileProps) => {
    const { show, onClose, file, clientId, refetchAll } = props;

    const initialValues = {
        name: file.name ?? '',
    };

    const renameMutation = useMutation({
        mutationFn: (payload: { name: string }) => renameDocumentFile(clientId, file._id, payload),
        onSuccess: (updatedFile) => {
            toast.success('File renamed successfully.');
            // onSuccess?.(updatedFile);
            refetchAll();
            onClose();
        },
        onError: (err: any) => {
            toast.error(err?.data?.message || err?.message || 'Some error occurred, please try again.');
        },
    });

    const handleSubmit = async (values: { name: string }) => {
        const name = values.name.trim();
        renameMutation.mutate({ name });
    };

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col h-[calc(100dvh-3rem)]">
                <h4>Rename File</h4>

                <div className="mt-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={RenameFileSchema}
                        enableReinitialize
                        onSubmit={handleSubmit}
                    >
                        <Form id="renameFileForm" className="flex flex-col gap-2">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <FormikInput name="name" placeholder="Enter file name" />

                                <FormHelperText variant="error">
                                    <ErrorMessage name="name" />
                                </FormHelperText>
                            </div>
                        </Form>
                    </Formik>
                </div>

                <div className="mt-auto">
                    <div className="flex gap-4 mt-6">
                        <Button type="button" size="sm" color="secondary" className="font-medium" onClick={onClose}>
                            Cancel
                        </Button>

                        <LoadingButton
                            type="submit"
                            size="sm"
                            form="renameFileForm"
                            isLoading={renameMutation.isPending}
                            loadingText="Saving"
                        >
                            Save
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default RenameFileDrawer;
